import React from 'react';
import { Box, Card, Typography, Chip } from '@mui/material';
import { Settings, Psychology, AutoAwesome, Assignment, Lightbulb, CheckCircle, Replay } from '@mui/icons-material';
import { renderValue, getLabel } from './interviewKitUtils';

const TYPE_COLORS = {
    technique: { bg: '#dbeafe', color: '#1d4ed8', icon: <Settings fontSize="small" /> },
    comportementale: { bg: '#ffedd5', color: '#c2410c', icon: <Psychology fontSize="small" /> },
    situationnelle: { bg: '#fce7f3', color: '#be185d', icon: <AutoAwesome fontSize="small" /> },
};
const TYPE_MAP = { technical: 'technique', behavioral: 'comportementale', situational: 'situationnelle' };

const KNOWN_KEYS = new Set([
    'question', 'title', 'titre', 'name',
    'expected_answer', 'answer', 'reponse', 'reponse_attendue',
    'criteria', 'evaluation', 'evaluation_criteria',
    'category', 'categorie', 'type',
    'difficulty', 'difficulte',
    'context', 'contexte', 'description',
    'follow_up', 'follow_up_questions',
]);

const DIFF_COLORS = {
    facile: { bg: '#d1fae5', color: '#065f46' },
    easy: { bg: '#d1fae5', color: '#065f46' },
    moyen: { bg: '#fef3c7', color: '#92400e' },
    medium: { bg: '#fef3c7', color: '#92400e' },
    difficile: { bg: '#fee2e2', color: '#991b1b' },
    hard: { bg: '#fee2e2', color: '#991b1b' },
};

export const QuestionCard = ({ item, idx }) => {
    const questionText = item.question || item.title || item.titre || item.name;
    const answerText = item.expected_answer || item.answer || item.reponse || item.reponse_attendue;
    const criteriaText = item.criteria || item.evaluation || item.evaluation_criteria;
    const categoryText = item.category || item.categorie;
    const typeText = item.type;
    const difficultyText = item.difficulty || item.difficulte;
    const contextText = item.context || item.contexte || item.description;
    const followUps = item.follow_up_questions || item.follow_up;

    const diff = DIFF_COLORS[difficultyText?.toLowerCase()] || null;

    let typeKey = typeText?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    typeKey = TYPE_MAP[typeKey] || typeKey;
    const typeStyle = TYPE_COLORS[typeKey] || null;

    const extraKeys = Object.keys(item).filter(
        k => !KNOWN_KEYS.has(k) && item[k] !== null && item[k] !== undefined && item[k] !== ''
    );

    return (
        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'visible' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.78rem' }}>{idx + 1}</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.5, fontSize: '0.97rem' }}>
                        {questionText || `Question ${idx + 1}`}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {typeText && typeStyle && (
                        <Chip icon={typeStyle.icon} label={typeText.charAt(0).toUpperCase() + typeText.slice(1)} size="small"
                            sx={{ backgroundColor: typeStyle.bg, color: typeStyle.color, fontWeight: 700, fontSize: '0.72rem', height: 22, '& .MuiChip-icon': { color: typeStyle.color } }} />
                    )}
                    {typeText && !typeStyle && (
                        <Chip label={typeText} size="small" sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
                    )}
                    {categoryText && <Chip label={categoryText} size="small" sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.72rem', height: 22 }} />}
                    {diff && difficultyText && <Chip label={difficultyText} size="small" sx={{ backgroundColor: diff.bg, color: diff.color, fontWeight: 600, fontSize: '0.72rem', height: 22 }} />}
                </Box>
            </Box>

            {/* Corps : contexte, réponse, critères, suivi, extras */}
            <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contextText && (
                    <Box sx={{ backgroundColor: '#fafafa', p: 2, borderRadius: 2, border: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Assignment fontSize="small" sx={{ mr: 0.5 }} /> Contexte
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>{contextText}</Typography>
                    </Box>
                )}
                {answerText && (
                    <Box sx={{ backgroundColor: '#eff6ff', p: 2, borderRadius: 2, borderLeft: '4px solid #6f42c1' }}>
                        <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Lightbulb fontSize="small" sx={{ mr: 0.5 }} /> Réponse attendue
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1e3a8a', lineHeight: 1.7 }}>
                            {Array.isArray(answerText) ? renderValue(answerText) : answerText}
                        </Typography>
                    </Box>
                )}
                {criteriaText && (
                    <Box sx={{ backgroundColor: '#f0fdf4', p: 2, borderRadius: 2, borderLeft: '4px solid #22c55e' }}>
                        <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <CheckCircle fontSize="small" sx={{ mr: 0.5 }} /> Critères d'évaluation
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#14532d', lineHeight: 1.7 }}>
                            {Array.isArray(criteriaText) ? renderValue(criteriaText) : criteriaText}
                        </Typography>
                    </Box>
                )}
                {followUps && (
                    <Box sx={{ backgroundColor: '#fff7ed', p: 2, borderRadius: 2, borderLeft: '4px solid #f97316' }}>
                        <Typography variant="caption" sx={{ color: '#ea580c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Replay fontSize="small" sx={{ mr: 0.5 }} /> Questions de suivi
                        </Typography>
                        {renderValue(Array.isArray(followUps) ? followUps : [followUps])}
                    </Box>
                )}
                {extraKeys.map(key => (
                    <Box key={key} sx={{ backgroundColor: '#f8fafc', p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                            {getLabel(key)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>{renderValue(item[key])}</Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
};

export default QuestionCard;
